import { FormikProps } from 'formik';

import { WithRemoved } from '@common/typescript/objects/WithRemoved';
import { WithId } from '@common/typescript/objects/WithId';
import { Nullable } from '@common/typescript/objects/Nullable';

import { isWithOrder, reorder, WithOrder } from '@app/objects/WithOrder';

type Entity = WithId & WithRemoved;

/**
 *  Utility-manager to perform basic operations over Formik form
 *  This is most suited for arrays stored in formik values
 */
export class FormikListManager<TForm, T extends Entity> {
	private readonly formik: FormikProps<TForm>;
	private list: Array<T> = [];
	private key: Nullable<string> = null;

	constructor(formik: FormikProps<TForm>) {
		this.formik = formik;
	}

	/**
	 * Wrap formik form / values with cache and list manager
	 * This allows to chain multiple operations and then apply them all as one change
	 * @param {FormikProps<TForm>} formik
	 * @returns {FormikListManager<TForm, T>}
	 */
	public static wrap<TForm, T extends Entity>(formik: FormikProps<TForm>): FormikListManager<TForm, T> {
		return new FormikListManager<TForm, T>(formik);
	}

	/**
	 * Set the key (path) of property from values that operations should be appled to
	 * @param {string} key
	 * @returns {FormikListManager<TForm, T>}
	 */
	public for(key: string): FormikListManager<TForm, T> {
		this.key = key;
		this.list = [...this.formik.getFieldMeta(key).value as Array<T>];

		return this;
	}

	/**
	 * Add a new item to current working list
	 * Item is added to the beginning of the list
	 * @param {T} item
	 * @returns {FormikListManager<TForm, T>}
	 */
	public add(item: T): FormikListManager<TForm, T> {
		this.list = [item, ...this.list];

		return this;
	}

	/**
	 * Add a new item to current working list
	 * Item is added to the end of 'existing' items list
	 * If list includes some 'soft deleted' items
	 * The new item is inserted after existing items and before 'soft deleted' once
	 *
	 * @param {T} item
	 * @returns {FormikListManager<TForm, T>}
	 */
	public push(item: T): FormikListManager<TForm, T> {
		const index = this.list.findIndex((item: T) => item.removed);
		if (index >= 0) {
			this.list = [...this.list.slice(0, index), item, ...this.list.slice(index)];
		} else {
			this.list = [...this.list, item];
		}

		if (isWithOrder(item)) {
			this.list = reorder(this.list as unknown as Array<WithOrder>) as unknown as Array<T>;
		}

		return this;
	}

	/**
	 * Soft remove specified item
	 * Item is marked as removed and moved to the end of the list
	 * @param {number} index
	 * @returns {FormikListManager<TForm, T>}
	 */
	public remove(index: number): FormikListManager<TForm, T> {
		if (index >= this.list.length) return this;

		const item = this.list[index];
		const result = this.list.filter((item: T, id: number) => id !== index);
		this.list = result;

		if (item.id <= 0) return this;

		result.push({ ...item, removed: true });

		return this;
	}

	/**
	 * Permanently delete item from the list
	 * @param {number} index
	 * @returns {FormikListManager<TForm, T>}
	 */
	public delete(index: number): FormikListManager<TForm, T> {
		if (index >= this.list.length) return this;

		this.list = this.list.filter((item: T, id: number) => id !== index);

		return this;
	}

	/**
	 * Set item at index to provided value
	 * @param {number} index
	 * @param {((value: T) => T) | T} item
	 * @returns {FormikListManager<TForm, T>}
	 */
	public set(index: number, item: T | ((value: T) => T)): FormikListManager<TForm, T> {
		if (index >= this.list.length) return this;

		this.list[index] = item instanceof Function ? item(this.list[index]) : item;

		return this;
	}

	/**
	 * Update item at index
	 * Meaning that existing value and provided values are merged together
	 * @param {number} index
	 * @param {Partial<T>} item
	 * @returns {FormikListManager<TForm, T>}
	 */
	public update(index: number, item: Partial<T>): FormikListManager<TForm, T> {
		if (index >= this.list.length) return this;

		this.list[index] = { ...this.list[index], ...item };

		return this;
	}

	/**
	 * Apply specified transformations to formik values
	 */
	public apply(): void {
		if (!this.key) return;

		this.formik.setFieldValue(this.key, this.list, false);
	}
}
